import axios from 'axios';
import { throttleAdapterEnhancer } from 'axios-extensions';

import { getDeviceId } from 'shared-services/device-service';
import { logError } from 'shared-services/logger-service';

import config from '../config';
import { showModal } from '../actions/modal';
import { getMasterToken, getTeamToken, getTeamUrl } from './store-service';
import { getPublicToken } from './public-share-service';
import { logout } from '../globals-service';
import { browserHistory } from './history';
import { getVersion } from './version-service';

const KNOWN_ERROR_CODES = [503, 440, 520, 441];

const axiosHttp = axios.create({
    adapter: throttleAdapterEnhancer(axios.defaults.adapter, { threshold: 1000 })
});

export const createRequestUrl = url => {
    return `${config.HOST}/${url}`;
}

export const validateResponse = (response, key) => {
    if (!response.data[key]) {
        throw new Error('Invalid response');
    }

    return response;
}

export default function AjaxService(requestConfig, useMasterToken, token) {
    const teamUrl = getTeamUrl();
    const authToken = token || (useMasterToken ? getMasterToken() : (getPublicToken() || getTeamToken(teamUrl)));

    const handleError = error => {
        if (error.field === 'x-session-token' || error.field === 'url') {
            return true;
        }

        if (error === 'To access this team you need to login via SSO') {
            showModal('SSO_LOGIN');

            return true;
        }

        switch ((error.response && error.response.type) || error.type) {
            case 'TeamMembersLimitError':
                showModal('SWITCH_PLAN', {
                    upgrade: 'members'
                });

                return true;

            case 'ProjectsLimitError':
                showModal('SWITCH_PLAN', {
                    upgrade: 'projects'
                });

                return true;

            case 'TimeFileLimitError':
                showModal('SWITCH_PLAN', {
                    upgrade: 'storageBytes'
                });

                return true;

            default:
                // eslint
        }

        if (error.response && error.response.status) {
            switch (error.response.status) {
                // case 503:
                case 440:
                case 520:
                    navigateToMaintenance(error.response.status);
                    throw error;

                case 441:
                    logout();
                    throw error;

                default:
            }
        }

        return false;
    }

    if (!requestConfig.hasOwnProperty('headers')) {
        requestConfig.headers = {};
    }

    if (!requestConfig.hasOwnProperty('params')) {
        requestConfig.params = {};
    }

    requestConfig.headers['Device-Id'] = getDeviceId();
    requestConfig.headers['APP-Version'] = getVersion();

    if (authToken) {
        requestConfig.headers['Authorization'] = `Bearer ${authToken}`;
    }

    if (requestConfig.data && requestConfig.data instanceof FormData) {
        requestConfig.headers['Content-Type'] = 'multipart/form-data';
    }

    requestConfig.url = requestConfig.url.indexOf('http') === 0 ? requestConfig.url : createRequestUrl(requestConfig.url);

    return axiosHttp(requestConfig)
        .then(response => {
            // trackTiming({
            //     type: 'AJAX_LOAD',
            //     time: new Date() - ajaxStartTimestamp,
            //     label: requestConfig.url
            // });

            if (!response.data) {
                return Promise.reject({
                    ...response,
                    response: response.data ? {
                        ...response.data,
                        status: response.data.statusCode
                    } : {
                        status: 'no data'
                    },
                    silent: true
                });
            }

            return response;
        })
        .catch(error => {
            if (handleError(error)) {
                return;
            }

            if (!(error.response && error.response.status && KNOWN_ERROR_CODES.includes(error.response.status))) {
                logError(`xhr ${error?.response?.status}`, error, {
                    requestConfig,
                    response: error.response
                });
            }

            throw error;
        });
}

let navigating = false;

const navigateToMaintenance = code => {
    if (navigating) {
        return;
    }

    browserHistory.push({
        pathname: `/maintenance`,
        state: { code }
    });

    navigating = setTimeout(() => {
        clearTimeout(navigating);
        navigating = false;
    }, 500);
}